/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import _get from "lodash/get"
import dayjs, { Dayjs, OpUnitType, QUnitType } from "dayjs"
import { ReviewReplyError } from "types/api/menuFeedback.type"

const language = {
  EN: "en",
  ID: "id",
  MS: "ms",
  TH: "th",
  VI: "vi",
  ZH: "zh",
}

type Unit = QUnitType | OpUnitType

export function getElapsedTimeInUnit(
  date: Dayjs,
  unit: Unit,
  lang?: string,
): string | boolean {
  const now = dayjs()
  const locale = lang || language.EN

  if (now.diff(date, unit) > 0) {
    if (locale === language.EN) {
      let elapsedTime = `${now.diff(date, unit)} ${unit}`

      if (now.diff(date, unit) === 1) {
        elapsedTime = elapsedTime.slice(0, elapsedTime.length - 1)
      }

      return elapsedTime
    }

    // return `${now.diff(date, unit)} ${locale.unit}`
  }

  return false
}

export function getElapsedTime(date: Dayjs): string {
  const units: Unit[] = [
    "seconds",
    "minutes",
    "hours",
    "days",
    "weeks",
    "months",
    "years",
  ]

  let elapsedTime = "A moment"

  units.forEach(unit => {
    if (getElapsedTimeInUnit(date, unit)) {
      elapsedTime = `${getElapsedTimeInUnit(date, unit)}`
    }
  })

  return `${elapsedTime} ago`
}

export function getElapsedWithLocale(
  date: Dayjs,
  textMap: { [key: string]: string } | undefined,
  options?: { dayjsLocale?: string; dateFormat?: string },
): string {
  let val = ""
  const now = dayjs()
  const hourDiff = now.diff(date, "hours")

  switch (true) {
    case hourDiff < 1:
      val = _get(textMap, "lessth1hr", "")
      break
    case hourDiff === 1: // lmao just because "1 hour ago" content :facepalm:
      val = _get(textMap, "exactly1hr", "")
      break
    case hourDiff <= 24: {
      const suffix = _get(textMap, "elapsedhours", "").replace(/<x>/gi, "")
      val = `${hourDiff.toString()}${suffix}`
      break
    }
    default: {
      const locale = options?.dayjsLocale || "en"
      const format = options?.dateFormat || "DD MMM YYYY"
      val = date.locale(locale).format(format)
      break
    }
  }

  return val
}

// custom error message outside form rules
// TODO: use intl later when language content provided
export const renderReplyErrorMessage = (errorCode: string): string => {
  switch (errorCode) {
    case ReviewReplyError.MERCHANT_IS_NOT_ROLLED_OUT_TO_MERCHANT_REPLIES:
      return "Merchant not rolled out to merchant replies."
    case ReviewReplyError.ROLE_NO_ACCESS_TO_MEX_REPLIES:
      return "Merchant role is not store manager or owner."
    case ReviewReplyError.REPLY_NOT_BELONG_TO_MERCHANT:
      return "The reply is not belong to the merchant."
    case ReviewReplyError.NON_PUBLIC_REVIEW_CANNOT_BE_REPLIED:
      return "Cannot replies to non-public review."
    case ReviewReplyError.CONTENT_LENGTH_LIMIT_EXCEEDED:
      return "Reply is too long."
    case ReviewReplyError.CONTENT_CONTAIN_PROFANITY:
      return "Let's keep the language appropriate."
    case ReviewReplyError.REPLY_COUNT_LIMIT_EXCEEDED:
      return "Exceeded reply count limit."
    case ReviewReplyError.EDIT_COUNT_LIMIT_EXCEEDED:
      return "Exceeded edit count limit."
    case ReviewReplyError.EDIT_DURATION_LIMIT_EXCEEDED:
      return "Exceeded edit duration limit."
    default:
      return errorCode
  }
}
